import { ArrowPathIcon, ArrowsPointingInIcon, ArrowsPointingOutIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ActionIcon, Box, Button, Group, Modal, Slider, Text } from '@mantine/core';
import mermaid from 'mermaid';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface MermaidChartProps {
  chart: string;
}

// Function to fix common Mermaid chart syntax issues
function fixChartSyntax(chartContent: string): string {
  let fixedChart = chartContent.trim();

  // Only apply minimal transformations for xychart-beta
  // Don't interfere with regular Mermaid charts like graph TD, flowchart, etc.

  // Check if it's already xychart format (both xychart-beta and xychart)
  if (fixedChart.startsWith('xychart-beta') || fixedChart.startsWith('xychart')) {
    // For Mermaid 11.x, keep it simple and don't over-process
    let result = fixedChart;

    // Handle both multi-line and single-line formats
    if (fixedChart.includes('\n')) {
      // Multi-line format - process each line
      const lines = fixedChart.split('\n');
      const fixedLines = lines.map(line => {
        // Just trim whitespace, don't modify content
        return line.trim();
      });

      result = fixedLines.join('\n');
    } else {
      // Single-line format (normalized) - ensure proper spacing between elements
      // Add spaces around key elements to ensure proper parsing
      result = fixedChart
        .replace(/(xychart-beta|xychart)/g, '$1 ')
        .replace(/(title)\s*"/g, '$1 "')
        .replace(/(x-axis)\s*\[/g, '$1 [')
        .replace(/(y-axis)\s*"/g, '$1 "')
        .replace(/(bar)\s*\[/g, '$1 [')
        // Ensure proper spacing after commas in arrays
        .replace(/,\s*"/g, ', "')
        .replace(/,\s*\[/g, ', [')
        .replace(/\]\s*"/g, '] "')
        .replace(/\]\s*\[/g, '] [');
    }

    // Ensure proper line endings
    result = result.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Remove any extra whitespace at the end
    result = result.trim();

    return result;
  }

  // Check if it's using "barChart" which is not a valid mermaid type
  if (fixedChart.startsWith('barChart')) {
    // Convert barChart to xychart-beta
    const lines = fixedChart.split('\n');
    let title = '';
    let xAxisTitle = '';
    let yAxisTitle = '';
    const categories: string[] = [];
    const values: number[] = [];

    for (const line of lines) {
      const trimmedLine = line.trim();

      if (trimmedLine.startsWith('title ')) {
        title = trimmedLine.replace('title ', '').replace(/"/g, '');
      } else if (trimmedLine.startsWith('x-axis ')) {
        xAxisTitle = trimmedLine.replace('x-axis ', '').replace(/"/g, '');
      } else if (trimmedLine.startsWith('y-axis ')) {
        yAxisTitle = trimmedLine.replace('y-axis ', '').replace(/"/g, '');
      } else if (trimmedLine.startsWith('bar ')) {
        // Parse bar data: "bar Java:87, Python:98, C++:95"
        const barData = trimmedLine.replace('bar ', '');
        const pairs = barData.split(',');

        for (const pair of pairs) {
          const [category, value] = pair.trim().split(':');
          if (category && value) {
            categories.push(category.trim());
            values.push(parseInt(value.trim(), 10) || 0);
          }
        }
      }
    }

    // Build xychart-beta
    let newChart = 'xychart-beta\n';
    if (title) {
      newChart += `title "${title}"\n`;
    }
    if (categories.length > 0) {
      newChart += `x-axis [${categories.map(c => `"${c}"`).join(', ')}]\n`;
    }
    if (yAxisTitle) {
      newChart += `y-axis "${yAxisTitle}"\n`;
    }
    if (values.length > 0) {
      newChart += `bar [${values.join(', ')}]\n`;
    }

    return newChart;
  }

  // Check if it's old bar chart syntax (very specific pattern)
  // Only convert if it has the exact old bar chart pattern
  if (fixedChart.includes('title') &&
    fixedChart.includes('axis x') &&
    fixedChart.includes('axis y') &&
    !fixedChart.startsWith('graph') &&
    !fixedChart.startsWith('flowchart') &&
    !fixedChart.startsWith('sequenceDiagram') &&
    !fixedChart.startsWith('classDiagram') &&
    !fixedChart.startsWith('stateDiagram') &&
    !fixedChart.startsWith('erDiagram') &&
    !fixedChart.startsWith('journey') &&
    !fixedChart.startsWith('gitgraph') &&
    !fixedChart.startsWith('pie') &&
    !fixedChart.startsWith('quadrantChart') &&
    !fixedChart.startsWith('xychart-beta') &&
    !fixedChart.startsWith('xychart')) {

    // Check if it's trying to be a bar chart but using wrong syntax
    const lines = fixedChart.split('\n');
    const hasBarChartIndicators = lines.some(line =>
      line.includes('title') ||
      line.includes('axis x') ||
      line.includes('axis y') ||
      (line.includes('"') && line.includes(':') && line.includes(' '))
    );

    if (hasBarChartIndicators) {
      // Convert to proper xychart syntax (using newer syntax)
      const titleMatch = fixedChart.match(/title\s+(.+)/);
      const xAxisMatch = fixedChart.match(/axis x\s+(.+)/);
      const yAxisMatch = fixedChart.match(/axis y\s+(.+)/);

      let newChart = 'xychart\n';
      if (titleMatch) {
        newChart += `title ${titleMatch[1]}\n`;
      }
      if (xAxisMatch) {
        newChart += `x-axis ${xAxisMatch[1]}\n`;
      }
      if (yAxisMatch) {
        newChart += `y-axis ${yAxisMatch[1]}\n`;
      }

      // Extract data points
      const dataLines = lines.filter(line =>
        line.includes('"') && line.includes(':') && line.includes(' ') &&
        !line.includes('title') && !line.includes('axis')
      );

      dataLines.forEach(line => {
        const match = line.match(/"([^"]+)":\s*(\d+)/);
        if (match) {
          const label = match[1];
          const value = parseInt(match[2]);
          newChart += `bar "${label}" ${value}\n`;
        }
      });

      return newChart;
    }
  }

  // For all other charts (including regular Mermaid charts), return as-is
  return fixedChart;
}

function getResponsiveSvg(svg: string): string {
  if (!svg) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svg, 'image/svg+xml');
    const svgEl = doc.querySelector('svg');
    if (!svgEl) return svg;
    svgEl.setAttribute('width', '100%');
    svgEl.setAttribute('height', '100%');
    svgEl.setAttribute('style', 'display: block; width: 100%; height: 100%;');
    svgEl.setAttribute('preserveAspectRatio', 'xMidYMin slice');
    if (!svgEl.getAttribute('viewBox')) {
      const w = svgEl.getAttribute('width') || '512';
      const h = svgEl.getAttribute('height') || '512';
      svgEl.setAttribute('viewBox', `0 0 ${parseFloat(w)} ${parseFloat(h)}`);
    }
    return svgEl.outerHTML;
  } catch (e) {
    return svg;
  }
}

function getSvgAspectRatio(svg: string): number {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svg, 'image/svg+xml');
    const svgEl = doc.querySelector('svg');
    if (!svgEl) return 4 / 3;
    let viewBox = svgEl.getAttribute('viewBox');
    if (viewBox) {
      const parts = viewBox.split(/\s+/);
      if (parts.length === 4) {
        const w = parseFloat(parts[2]);
        const h = parseFloat(parts[3]);
        if (w > 0 && h > 0) return w / h;
      }
    }

    const w = parseFloat(svgEl.getAttribute('width') || '800');
    const h = parseFloat(svgEl.getAttribute('height') || '600');
    if (w > 0 && h > 0) return w / h;
    return 4 / 3;
  } catch {
    return 4 / 3;
  }
}

// Function to convert xychart-beta to alternative chart format
function convertToAlternativeChart(chartContent: string): string {
  try {
    const lines = chartContent.split('\n');
    let title = 'Chart';
    let xAxisLabels: string[] = [];
    let barValues: number[] = [];

    // Parse the chart content
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine.startsWith('title')) {
        const titleMatch = trimmedLine.match(/title\s+"([^"]+)"/);
        if (titleMatch) {
          title = titleMatch[1];
        }
      } else if (trimmedLine.startsWith('x-axis')) {
        const xAxisMatch = trimmedLine.match(/x-axis\s+\[(.*)\]/);
        if (xAxisMatch) {
          xAxisLabels = xAxisMatch[1].split(',').map(label =>
            label.trim().replace(/"/g, '').replace(/'/g, '')
          );
        }
      } else if (trimmedLine.startsWith('bar')) {
        const barMatch = trimmedLine.match(/bar\s+\[(.*)\]/);
        if (barMatch) {
          barValues = barMatch[1].split(',').map(value =>
            parseInt(value.trim(), 10)
          );
        }
      }
    }

    // Create a simple bar chart using graph TD format
    if (xAxisLabels.length > 0 && barValues.length > 0) {
      let alternativeChart = `graph TD\n    title["${title}"]\n`;

      // Add bars
      for (let i = 0; i < Math.min(xAxisLabels.length, barValues.length); i++) {
        const label = xAxisLabels[i];
        const value = barValues[i];
        alternativeChart += `    bar${i}["${label}: ${value}"]\n`;
      }

      return alternativeChart;
    }

    // Fallback to simple text representation
    return `graph TD\n    title["Chart"]\n    note["Chart data could not be converted"]\n`;
  } catch (error) {
    console.error('Error converting chart:', error);
    return `graph TD\n    title["Chart"]\n    error["Error converting chart"]\n`;
  }
}

const ZOOM_MIN = 25;
const ZOOM_MAX = 200;
const ZOOM_STEP = 25;

// Helper function to clean markdown code fences from chart content
function cleanChartContent(content: string): string {
  let cleaned = content.trim();

  const codeBlockPattern = /^```(?:mermaid|language-mermaid)?\s*\n([\s\S]*?)\n?```$/;
  const match = cleaned.match(codeBlockPattern);

  if (match) {
    cleaned = match[1].trim();
  }

  const genericCodeBlockPattern = /^```\s*\n([\s\S]*?)\n?```$/;
  const genericMatch = cleaned.match(genericCodeBlockPattern);

  if (genericMatch && genericMatch[1].startsWith('xychart')) {
    cleaned = genericMatch[1].trim();
  }

  cleaned = cleaned.replace(/\n?\s*```\s*$/, '').trim();
  cleaned = cleaned.replace(/^\s*\.\.\.\s*$/gm, '');
  cleaned = cleaned.replace(/^\s*.+?-->\s*\.\.\.\s*$/gm, '');
  cleaned = cleaned.replace(/^\s*```\s*$/gm, '');
  cleaned = cleaned.replace(/^\s*.+?-->\s*```\s*$/gm, '');
  return cleaned.replace(/\n{3,}/g, '\n\n').trim();
}

export const MermaidChart: React.FC<MermaidChartProps> = ({ chart }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [svgContent, setSvgContent] = useState<string>('');
  const [isChartRendered, setIsChartRendered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartSvg, setChartSvg] = useState<string>('');
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef<{ x: number; y: number } | null>(null);
  const mouseStart = useRef<{ x: number; y: number } | null>(null);
  const diagramAreaRef = useRef<HTMLDivElement>(null);
  const [diagramAreaSize, setDiagramAreaSize] = useState({ width: 800, height: 600 });
  const [svgAspectRatio, setSvgAspectRatio] = useState(4 / 3);

  useEffect(() => {
    // Only initialize once to prevent multiple initializations
    if (!(window as any).mermaidInitialized) {
      try {
        mermaid.initialize({
          startOnLoad: false,
          theme: 'default',
          securityLevel: 'loose',
          fontFamily: 'arial',
          logLevel: 5, // Set to error only to reduce console noise
          // Add specific configuration for xychart-beta
          xyChart: {
            width: 800,
            height: 600,
          },
          // Add theme variables to properly support xychart
          themeVariables: {
            primaryColor: '#fff',
            primaryTextColor: '#333',
            primaryBorderColor: '#333',
            lineColor: '#333',
            secondaryColor: '#f5f5f5',
            tertiaryColor: '#fff',
            background: '#fff',
            // Specific xychart theme variables
            cScale0: '#1f77b4',
            cScale1: '#ff7f0e',
            cScale2: '#2ca02c',
            cScale3: '#d62728',
            cScale4: '#9467bd',
            cScale5: '#8c564b',
            cScale6: '#e377c2',
            cScale7: '#7f7f7f',
            cScale8: '#bcbd22',
            cScale9: '#17becf'
          }
        });
        (window as any).mermaidInitialized = true;
      } catch (error) {
        console.error('Error initializing Mermaid:', error);
      }
    }

    // Cleanup function to remove any mermaid error elements from DOM
    const cleanupInterval = setInterval(() => {
      // Remove any mermaid error SVGs that may have been inserted into the body
      const errorSvgs = document.querySelectorAll('body > svg[id*="mermaid"]');
      errorSvgs.forEach(svg => svg.remove());

      // Remove any error divs
      const errorDivs = document.querySelectorAll('body > div[id*="d"]');
      errorDivs.forEach(div => {
        if (div.textContent?.includes('Syntax error in text')) {
          div.remove();
        }
      });
    }, 5000); // Clean up every 5 seconds

    return () => clearInterval(cleanupInterval);
  }, []);

  const renderChart = async (chartContent: string): Promise<string> => {
    setError(null);
    try {
      // Try to fix common syntax issues first
      const fixedChart = fixChartSyntax(chartContent);

      const chartId = 'mermaid-' + Math.random().toString(36).substr(2, 9);

      try {
        const { svg } = await mermaid.render(chartId, fixedChart);
        setIsChartRendered(true);
        return svg;
      } catch (xychartError) {
        // If xychart-beta fails, try to convert to a regular bar chart
        if (fixedChart.startsWith('xychart-beta') || fixedChart.startsWith('xychart')) {
          const alternativeChart = convertToAlternativeChart(fixedChart);

          const altChartId = 'mermaid-alt-' + Math.random().toString(36).substr(2, 9);
          try {
            const { svg: altSvg } = await mermaid.render(altChartId, alternativeChart);
            setIsChartRendered(true);
            return altSvg;
          } catch (altError) {
            throw altError;
          }
        }

        throw xychartError;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(errorMessage);
      setIsChartRendered(false);
      return '';
    }
  };

  useEffect(() => {
    if (!chart) {
      setChartSvg('');
      setIsChartRendered(false);
      setError(null);
      return;
    }

    // Debounce chart rendering to avoid rendering incomplete charts during streaming
    const timeoutId = setTimeout(() => {
      const chartToRender = cleanChartContent(chart);

      // Only render if chart looks complete (has closing bar statement or similar)
      const isComplete = chartToRender.includes('bar [') ||
        chartToRender.includes('line [') ||
        !chartToRender.includes('xychart') ||
        (chartToRender.match(/\n/g) || []).length >= 3; // At least 3 lines

      if (isComplete) {
        setIsChartRendered(false);
        setError(null);
        renderChart(chartToRender).then(svg => {
          setChartSvg(svg);
        }).catch(err => {
          console.error('Chart rendering failed:', err);
        });
      }
    }, 300); // Wait 300ms after last update

    return () => clearTimeout(timeoutId);
  }, [chart]);

  const handleChartClick = async () => {
    setIsModalOpen(true);
    setZoomLevel(100);
  };

  useEffect(() => {
    if (isModalOpen && chart) {
      const chartToRender = cleanChartContent(chart);

      // Only render complete charts
      const isComplete = chartToRender.includes('bar [') ||
        chartToRender.includes('line [') ||
        !chartToRender.includes('xychart') ||
        (chartToRender.match(/\n/g) || []).length >= 3;

      if (isComplete) {
        renderChart(chartToRender).then(svg => {
          setSvgContent(svg);
        });
      }
    }
  }, [isModalOpen, chart]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + ZOOM_STEP, ZOOM_MAX));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - ZOOM_STEP, ZOOM_MIN));
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
  };

  const handleDownload = () => {
    if (!svgContent) return;
    const responsiveSvg = getResponsiveSvg(svgContent);
    const blob = new Blob([responsiveSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mermaid-chart.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isPanning || !mouseStart.current || !panStart.current) return;
    const dx = e.clientX - mouseStart.current.x;
    const dy = e.clientY - mouseStart.current.y;

    const areaW = diagramAreaRef.current?.clientWidth || 800;
    const areaH = diagramAreaRef.current?.clientHeight || 600;
    let baseW = areaW;
    let baseH = areaW / svgAspectRatio;
    if (baseH > areaH) {
      baseH = areaH;
      baseW = areaH * svgAspectRatio;
    }
    const scaledW = baseW * (zoomLevel / 100);
    const scaledH = baseH * (zoomLevel / 100);

    const maxPanX = Math.max(0, (scaledW - areaW) / 2);
    const maxPanY = Math.max(0, (scaledH - areaH) / 2);
    let newX = panStart.current.x + dx;
    let newY = panStart.current.y + dy;
    newX = Math.max(-maxPanX, Math.min(maxPanX, newX));
    newY = Math.max(-maxPanY, Math.min(maxPanY, newY));
    setPan({ x: newX, y: newY });
  };

  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoomLevel(prev => Math.min(prev + ZOOM_STEP, ZOOM_MAX));
    } else if (e.deltaY > 0) {
      setZoomLevel(prev => Math.max(prev - ZOOM_STEP, ZOOM_MIN));
    }
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsPanning(true);
    panStart.current = { ...pan };
    mouseStart.current = { x: e.clientX, y: e.clientY };
    document.body.style.userSelect = 'none';
  };

  const handleMouseUp = () => {
    setIsPanning(false);
    document.body.style.userSelect = '';
  };

  useEffect(() => {
    if (!isPanning) return;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPanning, zoomLevel]);

  useEffect(() => {
    setPan({ x: 0, y: 0 });
  }, [zoomLevel, isModalOpen]);

  useEffect(() => {
    function updateSize() {
      if (diagramAreaRef.current) {
        const rect = diagramAreaRef.current.getBoundingClientRect();
        setDiagramAreaSize({ width: rect.width, height: rect.height });
      }
    }
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [isModalOpen]);

  useEffect(() => {
    if (svgContent) {
      setSvgAspectRatio(getSvgAspectRatio(svgContent));
    }
  }, [svgContent]);

  return (
    <>
      <Box
        ref={containerRef}
        className="mermaid-chart"
        style={{
          cursor: 'pointer',
          position: 'relative',
          transition: 'all 0.2s ease',
          borderRadius: '8px',
          overflow: 'visible', // Changed from 'hidden' to 'visible' to show rotated labels
          border: '1px solid var(--mantine-color-gray-3)',
          backgroundColor: 'white',
          padding: '8px',
          minHeight: '100px',
        }}
        onClick={handleChartClick}
        title="Click to magnify"
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.02)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {!isChartRendered && !error && (
          <Text size="sm" c="dimmed" ta="center" py="md">
            Loading chart...
          </Text>
        )}
        {error && (
          <Text size="sm" c="red" ta="center" py="md">
            {error}
          </Text>
        )}
        {chartSvg && !error && (
          <div
            dangerouslySetInnerHTML={{ __html: getResponsiveSvg(chartSvg) }}
            style={{ width: '100%', height: '100%' }}
            className="mermaid-chart-container"
          />
        )}

        {/* Fallback display if chart fails to render */}
        {!chartSvg && !error && isChartRendered && (
          <Box p="md" ta="center">
            <Text size="sm" c="dimmed">
              Chart content detected but failed to render
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              Chart: {chart.substring(0, 100)}...
            </Text>
          </Box>
        )}
        <ActionIcon
          size="sm"
          variant="filled"
          color="blue"
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            zIndex: 10,
            opacity: 0.8,
            transition: 'opacity 0.2s ease',
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleChartClick();
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.8';
          }}
        >
          <MagnifyingGlassIcon width={14} height={14} />
        </ActionIcon>
      </Box>

      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fullScreen
        styles={{
          title: {
            fontWeight: 600,
          },
          content: {
            overflow: 'hidden',
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            maxHeight: '100vh',
            position: 'relative',
            padding: '20px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          },
          body: {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box style={{
          overflow: 'hidden',
          flex: 1,
          width: '100%',
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Group justify="space-between" mb="sm" style={{ flexShrink: 0 }}>
            <Text size="sm" c="dimmed">
              Use controls to zoom in/out and download the chart
            </Text>
          </Group>
          <Box mb="sm" style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            minWidth: 0,
            flexShrink: 0,
            flexGrow: 0,
          }}>
            <Box style={{ flex: '1 1 300px', minWidth: 180, maxWidth: 600, overflow: 'visible', marginLeft: '10px' }}>
              <Slider
                value={zoomLevel}
                onChange={setZoomLevel}
                min={ZOOM_MIN}
                max={ZOOM_MAX}
                step={ZOOM_STEP}
                label={(value) => `${value}%`}
                marks={[
                  { value: 25, label: '25%' },
                  { value: 100, label: '100%' },
                  { value: 200, label: '200%' },
                ]}
                style={{ width: '100%' }}
              />
            </Box>
            <Group align="center" style={{ flexWrap: 'wrap', gap: 8, minWidth: 0, overflowX: 'auto' }}>
              <Text size="sm" style={{ marginRight: 8 }}>{zoomLevel}%</Text>
              <ActionIcon
                variant="light"
                color="blue"
                onClick={handleZoomOut}
                disabled={zoomLevel <= ZOOM_MIN}
                title="Zoom out"
              >
                <ArrowsPointingInIcon width={16} height={16} />
              </ActionIcon>
              <ActionIcon
                variant="light"
                color="blue"
                onClick={handleZoomIn}
                disabled={zoomLevel >= ZOOM_MAX}
                title="Zoom in"
              >
                <ArrowsPointingOutIcon width={16} height={16} />
              </ActionIcon>
              <ActionIcon
                variant="light"
                color="blue"
                onClick={handleResetZoom}
                title="Reset zoom"
              >
                <ArrowPathIcon width={16} height={16} />
              </ActionIcon>
              <Button
                variant="light"
                color="blue"
                size="xs"
                onClick={handleDownload}
                title="Download as SVG"
              >
                Download
              </Button>
            </Group>
          </Box>
          <Box
            ref={diagramAreaRef}
            style={{
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: '8px',
              overflow: 'hidden',
              backgroundColor: 'white',
              position: 'relative',
              flex: '1 1 0',
              minHeight: 0,
              width: '100%',
              minWidth: 0,
              userSelect: isPanning ? 'none' : 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onWheel={handleWheel}
            tabIndex={0}
          >
            {!svgContent && (
              <Text size="sm" c="dimmed">
                Loading chart...
              </Text>
            )}
            {svgContent && (() => {
              const areaW = diagramAreaSize.width || 800;
              const areaH = diagramAreaSize.height || 600;

              // Calculate base dimensions to fit the container while maintaining aspect ratio
              let baseW = areaW;
              let baseH = areaW / svgAspectRatio;
              if (baseH > areaH) {
                baseH = areaH;
                baseW = areaH * svgAspectRatio;
              }

              // Apply zoom
              const scaledW = baseW * (zoomLevel / 100);
              const scaledH = baseH * (zoomLevel / 100);
              const enablePan = zoomLevel > 100 || scaledW > areaW || scaledH > areaH;

              return (
                <Box
                  ref={modalContainerRef}
                  style={{
                    width: `${scaledW}px`,
                    height: `${scaledH}px`,
                    transition: isPanning ? 'none' : 'width 0.2s, height 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px)`,
                    cursor: enablePan ? (isPanning ? 'grabbing' : 'grab') : 'default',
                    overflow: 'visible',
                    maxWidth: 'none',
                    maxHeight: 'none',
                    boxSizing: 'border-box',
                    pointerEvents: enablePan ? 'auto' : 'none',
                  }}
                  onMouseDown={enablePan ? handleMouseDown : undefined}
                  tabIndex={0}
                >
                  {svgContent && (
                    <div
                      dangerouslySetInnerHTML={{ __html: getResponsiveSvg(svgContent) }}
                      style={{ width: '100%', height: '100%' }}
                    />
                  )}
                </Box>
              );
            })()}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

// Add CSS styles for better chart label handling
const chartStyles = `
  .mermaid-chart-container svg {
    overflow: visible !important;
  }
  
  .mermaid-chart-container svg text {
    font-size: 11px !important;
    font-family: Arial, sans-serif !important;
  }
  
  .mermaid-chart-container svg .x-axis text,
  .mermaid-chart-container svg .xAxis text {
    transform: rotate(-45deg) !important;
    transform-origin: top left !important;
    text-anchor: end !important;
    dominant-baseline: hanging !important;
  }
  
  .mermaid-chart-container svg .y-axis text,
  .mermaid-chart-container svg .yAxis text {
    text-anchor: end !important;
    dominant-baseline: middle !important;
  }
  
  .mermaid-chart-container svg .chart-title {
    font-size: 14px !important;
    font-weight: bold !important;
    text-anchor: middle !important;
  }
`;

// Inject chart styles
const chartStyleElement = document.createElement('style');
chartStyleElement.textContent = chartStyles;
if (!document.head.querySelector('#mermaid-chart-styles')) {
  chartStyleElement.id = 'mermaid-chart-styles';
  document.head.appendChild(chartStyleElement);
} 
